import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Plus, Sparkles, ArrowUpRight, Loader2, ShieldCheck, X } from "lucide-react";

/**
 * Filings — creator side of the white-glove 8949 workflow.
 *
 * Gate: Studio tier only. Free/Pro/Team see an upsell CTA.
 * States rendered: submitted → in_review → drafting → ready_for_signature →
 *   filed → done  (+ cancelled).
 */

const STATUS_META = {
  submitted:            { label: "Submitted",            tone: "peach",   note: "In queue. A Quill CPA will begin review within 1 business day." },
  in_review:            { label: "In review",            tone: "peach",   note: "Our CPA is auditing your wallets & entity docs." },
  drafting:             { label: "Drafting",             tone: "mint",    note: "Preparing Form 8949 + Schedule D. Typically 2–4 business days." },
  ready_for_signature:  { label: "Ready for signature",  tone: "cream",   note: "Sign in to review and e-sign — nothing files with the IRS until you sign." },
  filed:                { label: "Filed with the IRS",   tone: "mint",    note: "Submitted. Awaiting IRS receipt." },
  done:                 { label: "Done",                 tone: "mint",    note: "Receipt & PDFs archived." },
  cancelled:            { label: "Cancelled",            tone: "pink",    note: "Cancelled." },
};

const ENTITY_LABELS = {
  individual: "Individual (1040 only)",
  sole_prop:  "Sole proprietor / Schedule C",
  llc:        "LLC (single or multi-member)",
  s_corp:     "S-Corp",
  c_corp:     "C-Corp",
};

const TONE_BG = {
  peach:  "quill-card-peach",
  mint:   "quill-card-mint",
  cream:  "quill-card-cream",
  pink:   "quill-card-pink",
};

const CURRENT_YEAR = new Date().getFullYear();

const Filings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const isStudio = (user?.subscription_tier || "").toLowerCase() === "studio";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/filings");
      setFilings(data.filings || []);
    } catch (e) {
      toast.error("Couldn't load filings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="filings-page">
      <PageHeader
        overline="Studio · Concierge"
        title="White-glove 8949 filing"
        description="Hand off your crypto tax filing to a real CPA. We prepare Form 8949 and Schedule D from your wallet activity. Nothing files with the IRS until you sign."
        actions={
          isStudio && (
            <button
              type="button"
              onClick={() => setShowForm(true)}
              data-testid="new-filing-btn"
              className="inline-flex items-center gap-2 bg-white text-black text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-200"
            >
              <Plus size={14} strokeWidth={2.5} /> New filing
            </button>
          )
        }
        testId="filings-header"
      />

      {!isStudio ? (
        <UpsellPanel onCta={() => navigate("/pricing")} />
      ) : (
        <FilingList filings={filings} loading={loading} onEmpty={() => setShowForm(true)} />
      )}

      <AnimatePresence>
        {showForm && (
          <IntakeModal
            onClose={() => setShowForm(false)}
            onSubmitted={async () => {
              setShowForm(false);
              await load();
              toast.success("Filing submitted — we'll email you when review begins.");
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Upsell ────────────────────────────────────────────────────────────────
const UpsellPanel = ({ onCta }) => (
  <div className="p-4 md:p-8" data-testid="filings-upsell">
    <div
      className="quill-card-cream rounded-2xl p-6 md:p-10 max-w-3xl relative overflow-hidden"
      style={{ color: "hsl(var(--quill-ink-warm))" }}
    >
      <div
        aria-hidden
        className="absolute -top-16 -right-10 w-48 h-48 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--quill-warm)) 0%, transparent 70%)", opacity: 0.35, filter: "blur(20px)" }}
      />
      <div className="l2-mono text-[10px] uppercase tracking-[0.22em] relative">
        Studio tier feature
      </div>
      <h2 className="font-display text-2xl md:text-3xl tracking-tight mt-2 relative">
        We&apos;ll file your 8949 for you.
      </h2>
      <p className="mt-4 max-w-lg text-[15px] leading-relaxed relative">
        Studio ($99/mo) includes a real CPA preparing your Form 8949 + Schedule D from your Quill
        wallet activity every tax year. E-sign in-app, and we submit to the IRS on your behalf.
      </p>
      <ul className="mt-5 space-y-2 relative">
        {[
          "Real CPA — not an AI-only workflow",
          "Multi-wallet + multi-chain reconciliation",
          "E-sign in-app, nothing files without your approval",
          "Cancel anytime — your data is always exportable",
        ].map((l) => (
          <li key={l} className="text-sm flex items-start gap-2">
            <ShieldCheck size={14} className="mt-1 shrink-0" />
            {l}
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onCta}
        data-testid="filings-upsell-cta"
        className="mt-7 inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium transition-transform hover:-translate-y-0.5"
        style={{ background: "hsl(var(--quill-ink-warm))", color: "#FFF6E5" }}
      >
        Upgrade to Studio <ArrowUpRight size={14} />
      </button>
    </div>
  </div>
);

// ── List ──────────────────────────────────────────────────────────────────
const FilingList = ({ filings, loading, onEmpty }) => {
  if (loading) {
    return (
      <div className="p-8 flex items-center gap-3 text-zinc-500 text-sm">
        <Loader2 size={14} className="animate-spin" /> Loading filings…
      </div>
    );
  }
  if (filings.length === 0) {
    return (
      <div className="p-4 md:p-8">
        <div
          className="border border-dashed border-[#2c241e] rounded-xl p-8 md:p-12 text-center max-w-2xl"
          data-testid="filings-empty"
        >
          <Sparkles size={20} className="mx-auto mb-3 text-amber-300/80" />
          <div className="font-display text-xl md:text-2xl tracking-tight">No filings yet</div>
          <p className="text-sm text-zinc-400 mt-2 max-w-md mx-auto">
            When you&apos;re ready, submit your entity + wallet info and a Quill CPA will take it from there.
          </p>
          <button
            type="button"
            onClick={onEmpty}
            data-testid="filings-empty-cta"
            className="mt-5 inline-flex items-center gap-2 bg-white text-black text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-200"
          >
            <Plus size={14} strokeWidth={2.5} /> Start a filing
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="p-4 md:p-8 space-y-4" data-testid="filings-list">
      {filings.map((f) => <FilingCard key={f.filing_request_id} filing={f} />)}
    </div>
  );
};

// ── Card ──────────────────────────────────────────────────────────────────
const FilingCard = ({ filing }) => {
  const meta = STATUS_META[filing.status] || STATUS_META.submitted;
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className="border border-[#2c241e] rounded-xl bg-[#1f1815] overflow-hidden"
      data-testid={`filing-card-${filing.filing_request_id}`}
    >
      <div className="p-4 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">
            Tax year {filing.tax_year} · {filing.legal_name}
          </div>
          <div className="font-display text-lg md:text-xl mt-1 tracking-tight">
            {ENTITY_LABELS[filing.entity_type] || filing.entity_type}
          </div>
          <div className="text-xs text-zinc-500 mt-1 font-mono">
            {filing.filing_request_id} · submitted {fmtDate(filing.submitted_at)}
          </div>
        </div>
        <div className={`${TONE_BG[meta.tone]} rounded-lg px-4 py-3 shrink-0`}>
          <div className="text-[10px] uppercase tracking-[0.22em] opacity-70" style={{ color: "hsl(var(--quill-ink-warm))" }}>
            Status
          </div>
          <div className="font-display text-base md:text-lg tracking-tight" style={{ color: "hsl(var(--quill-ink-warm))" }}>
            {meta.label}
          </div>
        </div>
      </div>
      <div className="px-4 md:px-6 pb-4 md:pb-6 -mt-2 text-sm text-zinc-400">
        {meta.note}
      </div>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        data-testid={`filing-toggle-${filing.filing_request_id}`}
        className="w-full border-t border-[#2c241e] py-3 text-[11px] uppercase tracking-[0.22em] text-zinc-500 hover:text-white hover:bg-[#241c17] transition-colors"
      >
        {expanded ? "Hide history" : "View history"}
      </button>
      {expanded && (
        <div className="border-t border-[#2c241e] p-4 md:p-6 space-y-3" data-testid={`filing-history-${filing.filing_request_id}`}>
          {(filing.status_history || []).slice().reverse().map((h, idx) => (
            <div key={idx} className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-amber-300/80 mt-1.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm">
                  {(STATUS_META[h.status] || {}).label || h.status}
                  <span className="text-zinc-500 ml-2 text-xs">{fmtDate(h.at)}</span>
                </div>
                {h.note && <div className="text-xs text-zinc-500 mt-0.5">{h.note}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Intake modal ──────────────────────────────────────────────────────────
const IntakeModal = ({ onClose, onSubmitted }) => {
  const [form, setForm] = useState({
    tax_year: CURRENT_YEAR - 1,
    legal_name: "",
    entity_type: "sole_prop",
    tax_id_last4: "",
    filing_state: "",
    notes: "",
  });
  const [wallets, setWallets] = useState([{ chain: "eth", address: "", label: "" }]);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateWallet = (i, k, v) =>
    setWallets((ws) => ws.map((w, idx) => (idx === i ? { ...w, [k]: v } : w)));
  const addWallet = () => setWallets((ws) => [...ws, { chain: "eth", address: "", label: "" }]);
  const removeWallet = (i) => setWallets((ws) => ws.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(form.tax_id_last4)) {
      toast.error("Tax ID last 4 must be exactly 4 digits");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/filings", {
        ...form,
        tax_year: Number(form.tax_year),
        wallet_addresses: wallets.filter((w) => w.address.trim().length > 0),
      });
      await onSubmitted();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submit failed. Check the form.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
        data-testid="intake-modal-scrim"
      />
      <motion.form
        onSubmit={submit}
        noValidate
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-x-0 top-8 md:top-16 bottom-0 md:bottom-8 z-50 max-w-2xl mx-auto rounded-t-2xl md:rounded-2xl bg-[#1f1815] border border-[#2c241e] overflow-y-auto"
        data-testid="intake-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2c241e] sticky top-0 bg-[#1f1815] z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300/80">Studio filing</div>
            <div className="font-display text-lg md:text-xl tracking-tight">Start a new 8949 filing</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-2 text-zinc-500 hover:text-white" data-testid="intake-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <Field label="Tax year">
            <select
              value={form.tax_year} onChange={update("tax_year")}
              className="w-full bg-[#1a1410] border border-[#2c241e] px-3 py-2 rounded-md text-sm"
              data-testid="intake-tax-year"
            >
              {[CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2, CURRENT_YEAR - 3].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </Field>

          <Field label="Legal name (as on tax return)">
            <input
              required minLength={2} value={form.legal_name} onChange={update("legal_name")}
              placeholder="e.g. Jane Q. Doe or Jane Doe LLC"
              className="w-full bg-[#1a1410] border border-[#2c241e] px-3 py-2 rounded-md text-sm"
              data-testid="intake-legal-name"
            />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Entity type">
              <select
                value={form.entity_type} onChange={update("entity_type")}
                className="w-full bg-[#1a1410] border border-[#2c241e] px-3 py-2 rounded-md text-sm"
                data-testid="intake-entity-type"
              >
                {Object.entries(ENTITY_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </Field>
            <Field label="Tax ID (last 4 only)" hint="Never send us your full SSN/EIN by chat.">
              <input
                required maxLength={4} inputMode="numeric" pattern="\d{4}"
                value={form.tax_id_last4} onChange={update("tax_id_last4")}
                placeholder="####"
                className="w-full bg-[#1a1410] border border-[#2c241e] px-3 py-2 rounded-md text-sm font-mono"
                data-testid="intake-tax-id-last4"
              />
            </Field>
          </div>

          <Field label="Filing state (optional)">
            <input
              value={form.filing_state} onChange={update("filing_state")}
              maxLength={2} placeholder="e.g. CA · NY · TX"
              className="w-full bg-[#1a1410] border border-[#2c241e] px-3 py-2 rounded-md text-sm uppercase"
              data-testid="intake-filing-state"
            />
          </Field>

          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Wallet addresses</div>
            <div className="space-y-2" data-testid="intake-wallets">
              {wallets.map((w, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <select
                    value={w.chain} onChange={(e) => updateWallet(i, "chain", e.target.value)}
                    className="col-span-3 bg-[#1a1410] border border-[#2c241e] px-2 py-2 rounded-md text-sm"
                    data-testid={`intake-wallet-chain-${i}`}
                  >
                    <option value="eth">ETH</option>
                    <option value="btc">BTC</option>
                  </select>
                  <input
                    value={w.address} onChange={(e) => updateWallet(i, "address", e.target.value)}
                    placeholder="0x… or bc1…"
                    className="col-span-6 bg-[#1a1410] border border-[#2c241e] px-2 py-2 rounded-md text-sm font-mono"
                    data-testid={`intake-wallet-addr-${i}`}
                  />
                  <input
                    value={w.label} onChange={(e) => updateWallet(i, "label", e.target.value)}
                    placeholder="Label"
                    className="col-span-2 bg-[#1a1410] border border-[#2c241e] px-2 py-2 rounded-md text-sm"
                    data-testid={`intake-wallet-label-${i}`}
                  />
                  <button
                    type="button" onClick={() => removeWallet(i)} aria-label="Remove"
                    className="col-span-1 text-zinc-500 hover:text-white p-1"
                    data-testid={`intake-wallet-remove-${i}`}
                  ><X size={14} /></button>
                </div>
              ))}
            </div>
            <button
              type="button" onClick={addWallet}
              className="mt-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white inline-flex items-center gap-1"
              data-testid="intake-wallet-add"
            >
              <Plus size={12} /> Add wallet
            </button>
          </div>

          <Field label="Notes for our CPA (optional)">
            <textarea
              value={form.notes} onChange={update("notes")}
              rows={3}
              placeholder="Anything unusual we should know — DAO income, hardware wallets, prior year issues, etc."
              className="w-full bg-[#1a1410] border border-[#2c241e] px-3 py-2 rounded-md text-sm"
              data-testid="intake-notes"
            />
          </Field>
        </div>
        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-[#2c241e] px-6 py-4 bg-[#1f1815]">
          <button type="button" onClick={onClose} className="text-sm text-zinc-400 hover:text-white px-3 py-2" data-testid="intake-cancel">
            Cancel
          </button>
          <button
            type="submit" disabled={submitting}
            className="inline-flex items-center gap-2 bg-white text-black text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-200 disabled:opacity-50"
            data-testid="intake-submit"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            Submit filing
          </button>
        </div>
      </motion.form>
    </>
  );
};

const Field = ({ label, hint, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">{label}</div>
    {children}
    {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
  </div>
);

export default Filings;
